import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jobListings } from '../data/jobsData';
import PageLayout from '../components/PageLayout';
import { ArrowLeft, MapPin, Briefcase, DollarSign, Calendar, Send, ChevronRight } from 'lucide-react';
import SEOHead from '../components/SEOHead';

export default function JobDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const job = jobListings.find(j => j.slug === slug);

  if (!job) {
    return (
      <PageLayout>
        <style>{`
          @media (max-width: 768px) {
            .job-application-form button[type="submit"] {
              width: 100% !important;
              min-height: 48px !important;
            }
          }
        `}</style>
        <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px' }}>
          <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', marginBottom: '16px' }}>Job not found</h2>
          <button onClick={() => navigate('/jobs')} style={{ backgroundColor: '#F97316', color: 'white', padding: '12px 28px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '15px', fontWeight: '600' }}>Back to Jobs</button>
        </div>
      </PageLayout>
    );
  }

  const otherJobs = jobListings.filter(j => j.slug !== slug).slice(0, 3);

  const inputStyle = {
    width: '100%', padding: '12px 16px', borderRadius: '10px',
    border: '1px solid #D1D5DB', background: 'white', fontSize: '15px',
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
    transition: 'border-color 0.2s ease',
  };

  return (
    <PageLayout>
      <style>{`
        @media (max-width: 768px) {
          .job-sidebar {
            position: static !important;
            margin-top: 32px;
          }
          .job-detail-grid {
            grid-template-columns: 1fr !important;
          }
          .job-application-form {
            padding: 24px !important;
          }
          .job-application-form button {
            width: 100% !important;
            min-height: 48px !important;
          }
        }
      `}</style>
      
      <SEOHead
        title={`${job.title} - ${job.location}`}
        description={`Join DISION Tech as ${job.title}. ${job.department} position in ${job.location}. Apply now for this exciting opportunity.`}
        keywords={`${job.title}, job opening, careers, ${job.department}, ${job.location}, Vietnam jobs`}
        url={`/jobs/${job.slug}`}
        type="article"
      />
      {/* Header */}
      <div style={{ background: '#F9FAFB', padding: '40px 24px 48px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <button
            onClick={() => navigate('/jobs')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '500', marginBottom: '24px', padding: 0 }}
            onMouseEnter={e => e.currentTarget.style.color = '#0099CC'}
            onMouseLeave={e => e.currentTarget.style.color = '#6B7280'}
          >
            <ArrowLeft size={16} /> Go back
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
            <h1 style={{ fontSize: 'clamp(24px, 3vw, 40px)', fontWeight: '800', color: '#111827', letterSpacing: '-0.5px', margin: 0 }}>{job.title}</h1>
            {job.tag && <span style={{ backgroundColor: '#FEF2F2', color: '#DC2626', padding: '3px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '700' }}>{job.tag}</span>}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
            {[
              { icon: MapPin, text: job.location },
              { icon: Briefcase, text: `${job.type} · ${job.department}` },
              { icon: DollarSign, text: job.salary },
              { icon: Calendar, text: job.postedDate },
            ].map(({ icon: Icon, text }, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Icon size={15} color="#6B7280" />
                <span style={{ fontSize: '14px', color: '#6B7280' }}>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="job-detail-grid" style={{ maxWidth: '1200px', margin: '0 auto', padding: '56px 24px 80px', display: 'grid', gridTemplateColumns: '1fr 380px', gap: '48px', alignItems: 'start' }}>
        {/* Left: Job details */}
        <div>
          {/* About */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '16px', paddingBottom: '12px', borderBottom: '2px solid #F3F4F6' }}>About</h2>
            <p style={{ fontSize: '16px', color: '#6B7280', lineHeight: 1.8 }}>{job.about}</p>
          </section>

          {/* Responsibilities */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '16px', paddingBottom: '12px', borderBottom: '2px solid #F3F4F6' }}>Responsibilities</h2>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {job.responsibilities.map((item, i) => (
                <li key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#F0F9FF', border: '2px solid #0099CC', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                    <svg width="8" height="6" viewBox="0 0 8 6" fill="none"><path d="M1 3l2 2 4-4" stroke="#0099CC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <span style={{ fontSize: '15px', color: '#374151', lineHeight: 1.7 }}>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Requirements */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '16px', paddingBottom: '12px', borderBottom: '2px solid #F3F4F6' }}>Requirements</h2>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {job.requirements.map((item, i) => (
                <li key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#FFF7ED', border: '2px solid #F97316', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                    <svg width="8" height="6" viewBox="0 0 8 6" fill="none"><path d="M1 3l2 2 4-4" stroke="#F97316" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <span style={{ fontSize: '15px', color: '#374151', lineHeight: 1.7 }}>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Tech Stack if available */}
          {job.techStack && (
            <section style={{ marginBottom: '40px' }}>
              <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '16px', paddingBottom: '12px', borderBottom: '2px solid #F3F4F6' }}>Our Tech Stack</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {job.techStack.map((tech, i) => (
                  <span key={i} style={{ padding: '6px 16px', borderRadius: '20px', background: '#F0F9FF', color: '#0099CC', fontSize: '13px', fontWeight: '600', border: '1px solid #BAE6FD' }}>{tech}</span>
                ))}
              </div>
            </section>
          )}

          {/* Benefits */}
          <section style={{ marginBottom: '0' }}>
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '16px', paddingBottom: '12px', borderBottom: '2px solid #F3F4F6' }}>Benefits</h2>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {job.benefits.map((item, i) => (
                <li key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#F0FDF4', border: '2px solid #16A34A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                    <svg width="8" height="6" viewBox="0 0 8 6" fill="none"><path d="M1 3l2 2 4-4" stroke="#16A34A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <span style={{ fontSize: '15px', color: '#374151', lineHeight: 1.7 }}>{item}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>

        {/* Right: Application form */}
        <div className="job-sidebar" style={{ position: 'sticky', top: '84px' }}>
          <div style={{ background: '#F9FAFB', borderRadius: '20px', padding: '32px', border: '1px solid #E5E7EB' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', marginBottom: '6px' }}>Apply for this position</h3>
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '24px' }}>Please fill out the form to submit your job application.</p>
            <form className="job-application-form" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }} id="job-application-form">
              <input type="hidden" name="jobTitle" value={job.title} />
              <input type="hidden" name="jobId" value={job.slug} />
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Full name *</label>
                <input type="text" name="fullName" placeholder="John Doe" required style={inputStyle} onFocus={e => e.target.style.borderColor = '#0099CC'} onBlur={e => e.target.style.borderColor = '#D1D5DB'} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Email *</label>
                <input type="email" name="email" placeholder="john@email.com" required style={inputStyle} onFocus={e => e.target.style.borderColor = '#0099CC'} onBlur={e => e.target.style.borderColor = '#D1D5DB'} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Phone *</label>
                <input type="tel" name="phone" placeholder="+84 xxx xxx xxx" required style={inputStyle} onFocus={e => e.target.style.borderColor = '#0099CC'} onBlur={e => e.target.style.borderColor = '#D1D5DB'} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Cover letter</label>
                <textarea name="coverLetter" placeholder="Tell us why you're a great fit..." rows={4} style={{...inputStyle, resize: 'vertical'}} onFocus={e => e.target.style.borderColor = '#0099CC'} onBlur={e => e.target.style.borderColor = '#D1D5DB'} />
              </div>
              <button
                type="submit"
                style={{ backgroundColor: '#F97316', color: 'white', padding: '13px 24px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '15px', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'background-color 0.2s ease' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#EA580C'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#F97316'}
              >
                Apply now <Send size={16} />
              </button>
            </form>
          </div>

          {/* Other positions */}
          {otherJobs.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: '700', color: '#111827', marginBottom: '16px' }}>Other open positions</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {otherJobs.map((j, i) => (
                  <button
                    key={i}
                    onClick={() => navigate(`/jobs/${j.slug}`)}
                    style={{ padding: '14px 16px', borderRadius: '10px', border: '1px solid #E5E7EB', background: 'white', cursor: 'pointer', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', transition: 'border-color 0.2s ease' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#0099CC'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#E5E7EB'}
                  >
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '3px', lineHeight: 1.3 }}>{j.title}</div>
                      <div style={{ fontSize: '12px', color: '#9CA3AF' }}>{j.location}</div>
                    </div>
                    <ChevronRight size={16} color="#9CA3AF" style={{ flexShrink: 0 }} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
}
